import { type HTMLAttributes, type Ref, useState, useId } from "react";
import { Button } from "../../atoms/Button";
import { Eye, EyeOff } from "../../atoms/Icon";
import { cn } from "../../utils";

export interface PasswordChangeCardProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  "onSubmit" | "title" | "onChange"
> {
  ref?: Ref<HTMLDivElement>;
  currentPasswordLabel?: string;
  currentPasswordPlaceholder?: string;
  newPasswordLabel?: string;
  newPasswordPlaceholder?: string;
  confirmPasswordLabel?: string;
  confirmPasswordPlaceholder?: string;
  submitButtonText?: string;
  values?: Partial<{
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }>;
  onChange?: (field: "currentPassword" | "newPassword" | "confirmPassword", value: string) => void;
  onSubmit?: (currentPassword: string, newPassword: string, confirmPassword: string) => void;
  isLoading?: boolean;
  errors?: Partial<Record<"currentPassword" | "newPassword" | "confirmPassword", string>>;
}

function PasswordChangeCard({
  className,
  ref,
  currentPasswordLabel = "현재 비밀번호",
  currentPasswordPlaceholder = "현재 비밀번호",
  newPasswordLabel = "새 비밀번호",
  newPasswordPlaceholder = "8자 이상 입력",
  confirmPasswordLabel = "비밀번호 확인",
  confirmPasswordPlaceholder = "비밀번호 재입력",
  submitButtonText = "비밀번호 변경",
  values,
  onChange,
  onSubmit,
  isLoading = false,
  errors = {},
  ...props
}: PasswordChangeCardProps) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const currentPasswordId = useId();
  const newPasswordId = useId();
  const confirmPasswordId = useId();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit?.(
      values?.currentPassword ?? currentPassword,
      values?.newPassword ?? newPassword,
      values?.confirmPassword ?? confirmPassword
    );
  };

  return (
    <div ref={ref} className={cn("w-full space-y-4", className)} {...props}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Current Password */}
        <div className="flex flex-col">
          <label
            htmlFor={currentPasswordId}
            className="text-sm font-medium text-[var(--text-tertiary)] mb-1.5"
          >
            {currentPasswordLabel}
          </label>
          <div className="relative">
            <input
              id={currentPasswordId}
              type={showCurrentPassword ? "text" : "password"}
              autoComplete="current-password"
              value={values?.currentPassword ?? currentPassword}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setCurrentPassword(e.target.value);
                onChange?.("currentPassword", e.target.value);
              }}
              placeholder={currentPasswordPlaceholder}
              disabled={isLoading}
              className={cn(
                "h-9 w-full rounded-lg border border-border-default bg-white px-3 pr-9 text-sm text-primary dark:border-dark-border-default dark:bg-dark-bg-secondary dark:text-dark-text-primary",
                "placeholder:text-placeholder dark:placeholder:text-dark-text-placeholder",
                "focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20",
                "disabled:opacity-50 disabled:cursor-not-allowed",
                errors.currentPassword &&
                  "border-error-brand focus:border-error-brand focus:ring-error-brand/20 dark:border-error-500 dark:focus:border-error-500 dark:focus:ring-error-500/20"
              )}
            />
            <button
              type="button"
              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              disabled={isLoading}
              className="absolute right-2 top-1/2 -translate-y-1/2 flex h-7 w-7 items-center justify-center rounded text-[var(--text-tertiary)] hover:text-[var(--text-primary)] disabled:opacity-50"
              aria-label={showCurrentPassword ? "비밀번호 숨기기" : "비밀번호 보기"}
              tabIndex={-1}
            >
              {showCurrentPassword ? <Eye size="sm" /> : <EyeOff size="sm" />}
            </button>
          </div>
          {errors.currentPassword && (
            <p className="text-xs text-error-500 mt-1">{errors.currentPassword}</p>
          )}
        </div>

        {/* New Password */}
        <div className="flex flex-col">
          <label
            htmlFor={newPasswordId}
            className="text-sm font-medium text-[var(--text-tertiary)] mb-1.5"
          >
            {newPasswordLabel}
          </label>
          <div className="relative">
            <input
              id={newPasswordId}
              type={showNewPassword ? "text" : "password"}
              autoComplete="new-password"
              value={values?.newPassword ?? newPassword}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setNewPassword(e.target.value);
                onChange?.("newPassword", e.target.value);
              }}
              placeholder={newPasswordPlaceholder}
              disabled={isLoading}
              className={cn(
                "h-9 w-full rounded-lg border border-border-default bg-white px-3 pr-9 text-sm text-primary dark:border-dark-border-default dark:bg-dark-bg-secondary dark:text-dark-text-primary",
                "placeholder:text-placeholder dark:placeholder:text-dark-text-placeholder",
                "focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20",
                "disabled:opacity-50 disabled:cursor-not-allowed",
                errors.newPassword &&
                  "border-error-brand focus:border-error-brand focus:ring-error-brand/20 dark:border-error-500 dark:focus:border-error-500 dark:focus:ring-error-500/20"
              )}
            />
            <button
              type="button"
              onClick={() => setShowNewPassword(!showNewPassword)}
              disabled={isLoading}
              className="absolute right-2 top-1/2 -translate-y-1/2 flex h-7 w-7 items-center justify-center rounded text-[var(--text-tertiary)] hover:text-[var(--text-primary)] disabled:opacity-50"
              aria-label={showNewPassword ? "비밀번호 숨기기" : "비밀번호 보기"}
              tabIndex={-1}
            >
              {showNewPassword ? <Eye size="sm" /> : <EyeOff size="sm" />}
            </button>
          </div>
          {errors.newPassword && (
            <p className="text-xs text-error-500 mt-1">{errors.newPassword}</p>
          )}
        </div>

        {/* Confirm Password */}
        <div className="flex flex-col">
          <label
            htmlFor={confirmPasswordId}
            className="text-sm font-medium text-[var(--text-tertiary)] mb-1.5"
          >
            {confirmPasswordLabel}
          </label>
          <div className="relative">
            <input
              id={confirmPasswordId}
              type={showConfirmPassword ? "text" : "password"}
              autoComplete="new-password"
              value={values?.confirmPassword ?? confirmPassword}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setConfirmPassword(e.target.value);
                onChange?.("confirmPassword", e.target.value);
              }}
              placeholder={confirmPasswordPlaceholder}
              disabled={isLoading}
              className={cn(
                "h-9 w-full rounded-lg border border-border-default bg-white px-3 pr-9 text-sm text-primary dark:border-dark-border-default dark:bg-dark-bg-secondary dark:text-dark-text-primary",
                "placeholder:text-placeholder dark:placeholder:text-dark-text-placeholder",
                "focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20",
                "disabled:opacity-50 disabled:cursor-not-allowed",
                errors.confirmPassword &&
                  "border-error-brand focus:border-error-brand focus:ring-error-brand/20 dark:border-error-500 dark:focus:border-error-500 dark:focus:ring-error-500/20"
              )}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              disabled={isLoading}
              className="absolute right-2 top-1/2 -translate-y-1/2 flex h-7 w-7 items-center justify-center rounded text-[var(--text-tertiary)] hover:text-[var(--text-primary)] disabled:opacity-50"
              aria-label={showConfirmPassword ? "비밀번호 숨기기" : "비밀번호 보기"}
              tabIndex={-1}
            >
              {showConfirmPassword ? <Eye size="sm" /> : <EyeOff size="sm" />}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="text-xs text-error-500 mt-1">{errors.confirmPassword}</p>
          )}
        </div>

        <Button type="submit" disabled={isLoading} className="w-full">
          {isLoading ? "변경 중…" : submitButtonText}
        </Button>
      </form>
    </div>
  );
}

export { PasswordChangeCard };
